import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft, ScanLine, Camera, Loader2 } from "lucide-react";

function extractToken(raw) {
  if (!raw) return null;
  // Accept both bare tokens and `prepmasters://friend-add?t=...` URLs.
  const m = raw.match(/(?:t=)([^&\s]+)/);
  return m ? m[1] : raw.trim();
}

export default function ScanQRPage() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [permissionExplained, setPermissionExplained] = useState(false);
  const [error, setError] = useState(null);
  const [handled, setHandled] = useState(false);

  const submitToken = async (token) => {
    if (handled || busy) return;
    setBusy(true);
    setHandled(true);
    try {
      const r = await api.post("/friends/scan", { token });
      if (r.data.status === "accepted") {
        toast.success("You're now friends!");
      } else {
        toast.success("Friend request sent");
      }
      nav("/student/friends");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
      setBusy(false);
      setHandled(false);
    }
  };

  const onScan = (result) => {
    if (!result || handled) return;
    const raw = Array.isArray(result) ? result[0]?.rawValue : result?.rawValue || result;
    const token = extractToken(typeof raw === "string" ? raw : raw?.text);
    if (token) submitToken(token);
  };

  const onError = (err) => {
    setError(err?.message || "Camera error");
  };

  return (
    <div className="max-w-md mx-auto space-y-6" data-testid="scan-qr-page">
      <Link to="/student/friends" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back to Friends
      </Link>

      <div className="text-center">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 inline-flex items-center gap-1.5">
          <ScanLine className="w-3.5 h-3.5" /> Scan a Friend
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Point at their QR</h1>
      </div>

      {!permissionExplained ? (
        <Card className="p-6 border-slate-200" data-testid="scan-permission-explainer">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 grid place-items-center rounded-md bg-amber-100 text-amber-700">
              <Camera className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">Camera permission</div>
              <div className="text-xs text-slate-500">We need camera access to scan a friend's QR.</div>
            </div>
          </div>
          <ul className="text-[13px] text-slate-700 space-y-1.5 list-disc ml-5">
            <li>We only read the QR — no photos are stored or uploaded</li>
            <li>Camera turns off the moment you leave this screen</li>
            <li>You can also paste a token below if you prefer</li>
          </ul>
          <Button
            className="w-full mt-5 bg-amber-600 hover:bg-amber-700"
            onClick={() => setPermissionExplained(true)}
            data-testid="scan-camera-allow-btn"
          >
            Open camera
          </Button>
        </Card>
      ) : (
        <Card className="p-4 border-slate-200" data-testid="scan-camera-card">
          <div className="aspect-square rounded-md overflow-hidden bg-black">
            <Scanner
              onScan={onScan}
              onError={onError}
              constraints={{ facingMode: "environment" }}
              components={{ finder: true, audio: false }}
              styles={{ container: { height: "100%", width: "100%" } }}
            />
          </div>
          {busy && (
            <div className="mt-3 text-center text-sm text-slate-600 flex items-center justify-center gap-2" data-testid="scan-sending-state">
              <Loader2 className="w-4 h-4 animate-spin" /> Sending request…
            </div>
          )}
          {error && (
            <div className="mt-3 text-center text-xs text-rose-600">{error}</div>
          )}
        </Card>
      )}

      <Card className="p-4 border-slate-200" data-testid="scan-manual-fallback">
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
          Or paste a token
        </div>
        <div className="flex gap-2">
          <Input
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
            placeholder="Paste the friend's token"
            data-testid="scan-manual-input"
          />
          <Button
            onClick={() => submitToken(extractToken(manualToken))}
            disabled={!manualToken.trim() || busy}
            data-testid="scan-manual-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
